import _ from 'lodash'
import moment from 'moment'
import brush from 'brush'
import { letters } from 'oderon-commons/src/utils'
import { dt, sDuration, mDistance, brDec } from 'oderon-commons/src/formatters'
import { preProcessSpec } from 'oderon-commons/src/tables'
import { smallTimeago } from '@/utils'

const hex = (n) => (n == null ? '-' : brush.toHexString(n))
const smallDate = (x) => (x ? moment(x).format('DD/MMM HH:mm') : '-')

function flagsColor(hb) {
  if (hb.receivedOn != null && moment().diff(hb.receivedOn, 'hours') >= 2) {
    return 'red'
  }

  let hasGps = _.get(hb, 'handshake.hasGps'),
    engineOn = _.get(hb, 'handshake.engineOn')

  if (!hasGps) return 'grey'
  if (engineOn) return 'green'
  return 'blue'
}

function rdColor(hb) {
  if (_.get(hb, 'rawData.length', 0) > 0) return 'green'
  return 'grey'
}

function renderAge(d) {
  if (d == null) return '-'

  // <span class="age">
  //   Jan/01
  //   <span class="small-timeago">(21D)</span>
  // </span>
  let strTime = moment(d).format('DD/MMM HH:mm'),
    strAgo = smallTimeago(d)

  let str = `
    <span class="age">
      ${strTime}
      <span class="small-timeago">${strAgo}</span>
    </span>`

  return str
}

function renderHbSummary(hb) {
  if (hb == null) return '-'
  let html = `
    ${renderAge(hb.receivedOn || hb.timestamp)}
    <span class="${flagsColor(hb)} indicator xs mx-1">F</span>
    <span class="${rdColor(hb)} indicator xs">RD</span>
  `
  return { html }
}

function getTagList(tags, highlightTags) {
  let s = _.map(tags, (t) => {
    if (_.some(highlightTags, (h) => letters(t) == letters(h))) {
      return `<span class="tag info">${t}</span>`
    } else {
      return `<span class="tag">${t}</span>`
    }
  })

  return `<span class="tag-list">${s.join('')}</span>`
}

export const defCodes = [
  'vehicleCode',
  'clientId',
  'brandModel',
  'tags',
  'description'
]

export const fullSpec = preProcessSpec([
  {
    t: 'Veículo',
    d: 'vehicle.code',
    code: 'vehicleCode',
    selectable: true
  },

  {
    t: 'Módulo',
    d: 'client.id',
    code: 'clientId',
    selectable: true
  },

  {
    t: 'Marca/Modelo',
    d: 'vehicle.extraInfo.brandModel.name',
    code: 'brandModel',
    selectable: true
  },

  {
    t: 'Tags',
    code: 'tags',
    selectable: true,
    d: (row, idx, spec) => {
      let tags = _.get(row, 'vehicle.tags')
      if (tags == null || tags.length < 1) return '-'

      let highlightTags = []
      if (spec.tagMode == 'highlight') highlightTags = spec.selectedTags

      return { html: getTagList(tags, highlightTags) }
    }
  },

  {
    t: 'Descrição',
    d: 'vehicle.description',
    code: 'description',
    selectable: true
  },

  {
    t: 'Horímetro (h)|',
    d: 'vehicle.horometer',
    code: 'horometer',
    f: (x) => brDec(x / 3600),
    selectable: true
  },

  {
    t: 'Odômetro (km)|',
    d: 'vehicle.odometer',
    code: 'odometer',
    f: (x) => brDec(x / 1000),
    selectable: true
  },

  {
    t: 'Firmware|',
    code: 'fw',
    d: 'client.currentFirmwareVersion',
    f: hex,
    selectable: true
  },

  {
    t: 'Lote|',
    d: 'client.batchNumber',
    selectable: true
  },

  {
    t: '|Observações',
    d: 'client.comments',
    selectable: true,
    sortBy: false
  },

  {
    t: 'SimCard|',
    code: 'iccid',
    d: 'client.simCard.iccid',
    selectable: true
  },

  {
    t: 'Operadora|',
    code: 'client.operator',
    selectable: true,
    d: 'simCard.operator'
  },

  {
    t: 'Último HB|',
    code: 'lastHb',
    selectable: true,
    sortBy: 'lastHb.receivedOn',
    needsHealth: true,
    d: (row) => renderHbSummary(_.get(row, 'client.lastHb'))
  },

  {
    t: 'Último HB consumido|',
    code: 'lastConsumedHb',
    selectable: true,
    sortBy: 'lastConsumedHb.timestamp',
    needsHealth: true,
    d: (row) => {
      let lastC = _.get(row, 'client.lastConsumedHb'),
        lastId = _.get(row, 'client.lastHb.id')

      if (lastC == null) return '-'
      if (lastC.id == lastId) return '(igual ao mais recente)'
      return renderHbSummary(lastC)
    }
  },

  {
    t: 'Último HB com rawdata|',
    code: 'lastRawData',
    selectable: true,
    sortBy: 'lastHbWithRawdata.timestamp',
    needsHealth: true,
    d: (row) => {
      let lastR = _.get(row, 'client.lastHbWithRawdata'),
        lastId = _.get(row, 'client.lastHb.id')

      if (lastR == null) return '-'
      if (lastR.id == lastId) return '(igual ao mais recente)'
      return renderHbSummary(lastR)
    }
  },

  {
    t: 'Último trecho|',
    code: 'lastValidTrip',
    selectable: true,
    sortBy: 'lastValidTrip.startTimestamp',
    needsHealth: true,
    d: (row) => {
      let trip = _.get(row, 'vehicle.data.lastValidTrip')
      if (trip == null) return '-'

      let html = `
        <span class="driver-name">${trip.driver.name}</span>
        <span class="fixed-1">${smallDate(trip.startTimestamp)}</span>
        <span class="fixed-1">${sDuration(trip.durationS)}</span>
        <span class="fixed-1">${mDistance(trip.distanceM)}km</span>
      `
      return { html }
    }
  },

  {
    t: 'Criado em|',
    d: 'vehicle.createdAt',
    code: 'createdAt',
    selectable: true,
    f: (x) => dt(x, 'YYYY-MMM-DD HH:mm')
  }
])

export const fullSpec2 = preProcessSpec({
  defCodes: ['id', 'code', 'extraCode', 'clientId', 'brandModel'],
  columns: [
    {
      code: 'id',
      t: 'ID (repo)',
      d: 'id',
      selectable: true,
      color: 'grey'
    },
    {
      t: 'ID (org)',
      d: 'idInOrg',
      selectable: true
    },
    {
      t: 'Veículo',
      d: 'code'
    },
    {
      t: 'Cód. Aux.',
      d: 'extraCode',
      selectable: true
    },
    {
      t: 'Grupo',
      d: 'group',
      selectable: true
    },
    {
      t: 'Módulo',
      d: 'clientId'
    },
    {
      t: 'Marca/Modelo',
      d: 'brandModel.name',
      code: 'brandModel',
      selectable: true
    },
    {
      t: 'Tags',
      d: 'tags',
      selectable: true
    },
    {
      t: 'Descrição',
      d: 'description',
      selectable: true
    },
    {
      t: 'Criado em|',
      d: 'createdAt',
      selectable: true,
      f: (x) => dt(x, 'YYYY-MM-DD HH:mm')
    },
    {
      t: 'Atualizado em|',
      d: 'updatedAt',
      selectable: true,
      f: (x) => dt(x, 'YYYY-MM-DD HH:mm')
    },
    {
      t: 'Desmobilizado em|',
      d: 'deletedAt',
      selectable: true,
      f: (x) => dt(x, 'YYYY-MM-DD HH:mm')
    }
  ]
})
